import { useEffect, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Badge } from '@/shared/components/ui/badge'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { Search, Plus, X, Shield, FlaskConical } from 'lucide-react'
import { toast } from 'sonner'
import * as areasApi from '@/modules/users/api/areasApi'
import { apiClient } from '@/shared/services/client'
import { areasKeys } from '@/modules/users/hooks/useAreas'
import type { AreaGrupo } from '@/modules/users/types/area'

interface SimpleGroup {
  id: number
  name: string
}

interface AreaGroupsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  areaId: number | null
  areaName: string
}

export function AreaGroupsDialog({
  open,
  onOpenChange,
  areaId,
  areaName,
}: AreaGroupsDialogProps) {
  const queryClient = useQueryClient()
  const [groupSearch, setGroupSearch] = useState('')
  const [searchResults, setSearchResults] = useState<SimpleGroup[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [isAdding, setIsAdding] = useState(false)

  const { data: areaGroups, isLoading } = useQuery({
    queryKey: ['area-groups', areaId],
    queryFn: () => areasApi.getAreaGroups(areaId!),
    enabled: open && !!areaId,
  })

  useEffect(() => {
    if (!open) {
      setGroupSearch('')
      setSearchResults([])
    }
  }, [open])

  // Search groups with debounce
  useEffect(() => {
    if (groupSearch.length < 2) {
      setSearchResults([])
      return
    }

    const timer = setTimeout(async () => {
      setIsSearching(true)
      try {
        const { data } = await apiClient.get('/group-extensions/', {
          params: { search: groupSearch, page_size: 15 },
        })
        const results = (data.results || []) as SimpleGroup[]
        // Filter out already assigned groups
        const assignedIds = new Set(areaGroups?.map((g: AreaGrupo) => g.id) || [])
        setSearchResults(results.filter((g: SimpleGroup) => !assignedIds.has(g.id)))
      } catch {
        setSearchResults([])
      }
      setIsSearching(false)
    }, 300)

    return () => clearTimeout(timer)
  }, [groupSearch, areaGroups])

  const handleAdd = async (groupId: number) => {
    if (!areaId) return
    setIsAdding(true)
    try {
      await areasApi.addGroupToArea(areaId, groupId)
      queryClient.invalidateQueries({ queryKey: ['area-groups', areaId] })
      queryClient.invalidateQueries({ queryKey: areasKeys.lists() })
      queryClient.invalidateQueries({ queryKey: areasKeys.allList() })
      setSearchResults((prev) => prev.filter((g) => g.id !== groupId))
      toast.success('Grupo agregado')
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al agregar grupo')
    }
    setIsAdding(false)
  }

  const handleRemove = async (groupId: number, isLab?: boolean) => {
    if (!areaId) return
    if (isLab) {
      toast.error('El laboratorio se gestiona desde la edición del área')
      return
    }
    try {
      await areasApi.removeGroupFromArea(areaId, groupId)
      queryClient.invalidateQueries({ queryKey: ['area-groups', areaId] })
      queryClient.invalidateQueries({ queryKey: areasKeys.lists() })
      queryClient.invalidateQueries({ queryKey: areasKeys.allList() })
      toast.success('Grupo removido')
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al remover grupo')
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-teal-500" />
            Grupos de {areaName}
          </DialogTitle>
          <DialogDescription>
            Gestiona los grupos de permisos asignados a esta área
          </DialogDescription>
        </DialogHeader>

        {/* Search to add groups */}
        <div className="space-y-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Buscar grupo por nombre..."
              value={groupSearch}
              onChange={(e) => setGroupSearch(e.target.value)}
              className="pl-9"
            />
          </div>

          {/* Search results */}
          {(searchResults.length > 0 || isSearching) && (
            <div className="rounded-md border bg-muted/30 max-h-40 overflow-y-auto">
              {isSearching ? (
                <div className="p-3 text-center text-sm text-muted-foreground">Buscando...</div>
              ) : (
                searchResults.map((group) => (
                  <div
                    key={group.id}
                    className="flex items-center justify-between p-2 hover:bg-accent"
                  >
                    <div className="text-sm font-medium truncate">{group.name}</div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleAdd(group.id)}
                      disabled={isAdding}
                      className="ml-2 h-7 text-teal-600 hover:text-teal-700 hover:bg-teal-500/10"
                    >
                      <Plus className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Current groups */}
        <div className="space-y-2">
          <div className="text-sm font-medium text-muted-foreground">
            Grupos asignados ({areaGroups?.length || 0})
          </div>

          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : areaGroups && areaGroups.length > 0 ? (
            <div className="rounded-md border max-h-[300px] overflow-y-auto">
              {areaGroups.map((group) => (
                <div
                  key={group.id}
                  className="flex items-center justify-between p-2 border-b last:border-b-0 hover:bg-accent"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    {group.is_laboratorio ? (
                      <FlaskConical className="h-3.5 w-3.5 text-blue-500 flex-shrink-0" />
                    ) : (
                      <Shield className="h-3.5 w-3.5 text-teal-500 flex-shrink-0" />
                    )}
                    <span className="text-sm font-medium truncate">{group.name}</span>
                    {group.is_laboratorio && (
                      <Badge variant="outline" className="text-xs">Lab</Badge>
                    )}
                  </div>
                  {!group.is_laboratorio && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleRemove(group.id, group.is_laboratorio)}
                      className="ml-2 h-7 text-destructive hover:text-destructive hover:bg-destructive/10"
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="p-6 text-center text-sm text-muted-foreground rounded-md border">
              Sin grupos asignados
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
